import { create } from "zustand";

import {
  getLastPointerPoint,
  interruptPointerTracking,
  isPhysicalPointerDown,
  startPointerTracking,
  stopPointerTracking,
} from "./pointerTracker";
import {
  canClaimGlobalKey,
  isEditableElement,
  isKeyboardActivatableTarget,
  isTypingTarget,
} from "./guards";
import { onPressInterrupt, type PressInterruptReason } from "./interrupts";
import type {
  DrawLockMode,
  DrawLockReleaseReason,
  DrawPoint,
  DrawSurfaceAdapter,
  DrawSurfaceToken,
} from "./types";

/**
 * A press shorter than this is a tap (latches the lock on); anything longer is
 * a hold (releases on key-up). Also used for the hold-S push-to-talk split.
 *
 * Generous because optimistic capture makes the window free: the UI flips and
 * the mic buffers at key-down, so only the irreversible turn open waits. 200ms
 * clears a deliberate key tap (typically 60-120ms) by a comfortable margin.
 */
export const TAP_THRESHOLD_MS = 200;

const SPACE_CODES = ["Space"] as const;

interface Registration {
  token: DrawSurfaceToken;
  adapter: DrawSurfaceAdapter;
  active: boolean;
  /** Monotonic activation counter — breaks ties when several are active. */
  activatedAt: number;
}

/** Render-relevant state only. Everything else stays out of React. */
interface DrawLockState {
  mode: DrawLockMode;
  /** id of the surface currently holding the pen down, if any. */
  engagedSurfaceId: string | null;
}

export const useDrawLockStore = create<DrawLockState>(() => ({
  mode: "off",
  engagedSurfaceId: null,
}));

const registry = new Map<DrawSurfaceToken, Registration>();

/**
 * Held directly rather than looked up, so a release always reaches the surface
 * that received the engage — even if it has since been deactivated.
 */
let engaged: { token: DrawSurfaceToken; adapter: DrawSurfaceAdapter } | null =
  null;

let spacePressStartedAt = 0;
/**
 * True between a Space key-down we claimed and its key-up. The key-up must NOT
 * re-run the typing guards: if focus moves mid-press they would flip and we
 * would never release, leaving a stuck stroke.
 */
let spaceOwned = false;
let activationCounter = 0;
let listenerRefCount = 0;

function setState(next: Partial<DrawLockState>) {
  useDrawLockStore.setState(next);
}

export function getDrawLockMode(): DrawLockMode {
  return useDrawLockStore.getState().mode;
}

/**
 * Active surfaces, most recently activated first.
 *
 * More than one can be active (the tutor page mounts scratchpad and notepad
 * together), and recency is only a preference — callers walk the list and take
 * the first that accepts, so a mounted-but-unhovered surface can't starve the
 * one under the cursor.
 */
function getActiveRegistrations(): Registration[] {
  const active: Registration[] = [];
  for (const reg of registry.values()) {
    if (reg.active) active.push(reg);
  }
  return active.sort((a, b) => b.activatedAt - a.activatedAt);
}

/**
 * Try to hand the pen to one surface at `point`. Press-wide preconditions are
 * checked once by the caller — they cannot change while walking candidates.
 */
function engageAt(reg: Registration, point: DrawPoint): boolean {
  if (!reg.adapter.containsPoint(point)) return false;
  if (!reg.adapter.canEngage()) return false;

  let ok = false;
  try {
    ok = reg.adapter.engage(point);
  } catch {
    ok = false;
  }
  if (!ok) return false;

  engaged = { token: reg.token, adapter: reg.adapter };
  setState({ engagedSurfaceId: reg.adapter.id });
  return true;
}

/**
 * Ends the locked stroke if there is one and returns the machine to 'off'.
 * Idempotent and synchronous — safe to call from unmount and pagehide.
 */
export function releaseDrawLock(
  reason: DrawLockReleaseReason = "forced",
  cancel = false,
): void {
  const current = engaged;
  engaged = null;
  if (current) {
    try {
      current.adapter.release(reason, cancel);
    } catch {
      // A failing adapter must not leave the machine latched.
    }
  }
  if (useDrawLockStore.getState().mode !== "off" || current) {
    setState({ mode: "off", engagedSurfaceId: null });
  }
}

function onKeyDown(e: KeyboardEvent) {
  // Field-scoped, not page-wide: isTypingTarget is also true for every target
  // while the on-screen keyboard is anywhere in the DOM, which would stop
  // Escape releasing the lock from the canvas whenever the LaTeX panel is open.
  // That page-wide rule belongs to Space below, where it is the whole point.
  if (e.code === "Escape" || e.key === "Escape") {
    if (!isEditableElement(e.target) && getDrawLockMode() !== "off") {
      releaseDrawLock("escape");
    }
    return;
  }
  if (e.code !== "Space") return;

  const typing = isTypingTarget(e.target);

  // Auto-repeat while holding: keep consuming so the page never scrolls, but
  // do not re-engage.
  if (e.repeat) {
    if (spaceOwned) e.preventDefault();
    return;
  }

  const mode = getDrawLockMode();

  // Latched -> tap to exit. This is an event we own, so it must be consumed;
  // otherwise the browser scrolls the page on the way out.
  if (mode === "latched") {
    if (typing) return;
    e.preventDefault();
    spaceOwned = true;
    releaseDrawLock("toggle-off");
    return;
  }

  if (mode !== "off") return;

  // No canvas at all -> Space keeps its normal browser behaviour.
  const candidates = getActiveRegistrations();
  if (candidates.length === 0) return;

  // Space stays with focused buttons/links so keyboard activation still works,
  // and with the OS on Cmd/Ctrl/Alt — except over a surface, whose own
  // controls never veto the lock: clicking a tool leaves it focused, and a
  // press there means "draw", not "activate that button again".
  const target = e.target instanceof Element ? e.target : null;
  const targetOnSurface =
    target !== null &&
    // Ownership only ever waives the focused-control rule, so it is asked only
    // when that rule is what would stand us down.
    isKeyboardActivatableTarget(target) &&
    candidates.some((reg) => reg.adapter.ownsElement?.(target) === true);
  if (
    !canClaimGlobalKey(e, {
      codes: SPACE_CODES,
      respectActivatableTarget: !targetOnSurface,
    })
  ) {
    return;
  }

  // Press-wide preconditions, evaluated once rather than per candidate.
  if (engaged) return;
  if (isPhysicalPointerDown()) return;
  const point = getLastPointerPoint();
  if (!point) return;

  if (!candidates.some((reg) => engageAt(reg, point))) return;

  e.preventDefault();
  spaceOwned = true;
  spacePressStartedAt = Date.now();
  setState({ mode: "held" });
}

function onKeyUp(e: KeyboardEvent) {
  if (e.code !== "Space") return;
  // Ownership, not target inspection, decides whether this key-up is ours.
  if (!spaceOwned) return;
  e.preventDefault();
  spaceOwned = false;

  if (getDrawLockMode() !== "held") return;

  if (Date.now() - spacePressStartedAt < TAP_THRESHOLD_MS) {
    // A tap: keep the pen down and stay latched until the next tap.
    setState({ mode: "latched" });
    return;
  }
  releaseDrawLock("key-up");
}

/**
 * "The press can no longer be completed": drop space-bar ownership, void the
 * pointer tracker's own press (its pointerup may never arrive either), and end
 * any locked stroke. The event list itself lives in `interrupts`, shared with
 * the hold-key machine and the scratchpad.
 */
function onInterrupt(reason: PressInterruptReason) {
  spaceOwned = false;
  interruptPointerTracking();
  if (getDrawLockMode() !== "off") releaseDrawLock(reason);
}

let unsubscribeInterrupts: (() => void) | null = null;

function addGlobalListeners() {
  globalThis.addEventListener("keydown", onKeyDown);
  globalThis.addEventListener("keyup", onKeyUp);
  unsubscribeInterrupts = onPressInterrupt(onInterrupt);
  startPointerTracking();
}

function removeGlobalListeners() {
  globalThis.removeEventListener("keydown", onKeyDown);
  globalThis.removeEventListener("keyup", onKeyUp);
  unsubscribeInterrupts?.();
  unsubscribeInterrupts = null;
  stopPointerTracking();
}

/**
 * Registers a drawable surface. Cleanup is keyed by the returned token, so a
 * stale component tearing down can never unregister a newer surface.
 */
export function registerDrawSurface(
  adapter: DrawSurfaceAdapter,
): DrawSurfaceToken {
  const token: DrawSurfaceToken = { id: adapter.id };
  registry.set(token, { token, adapter, active: false, activatedAt: 0 });
  if (listenerRefCount === 0) addGlobalListeners();
  listenerRefCount += 1;
  return token;
}

export function unregisterDrawSurface(token: DrawSurfaceToken): void {
  const reg = registry.get(token);
  if (!reg) return;
  registry.delete(token);
  // Discard rather than commit: the surface is going away, so a committed
  // stroke could land on a canvas that no longer exists.
  if (engaged?.token === token) releaseDrawLock("unregister", true);
  listenerRefCount = Math.max(0, listenerRefCount - 1);
  if (listenerRefCount === 0) removeGlobalListeners();
}

/** Marks a surface as the one the space bar should drive. */
export function setDrawSurfaceActive(
  token: DrawSurfaceToken,
  active: boolean,
): void {
  const reg = registry.get(token);
  if (!reg) return;
  if (reg.active === active) return;
  reg.active = active;
  if (active) {
    activationCounter += 1;
    reg.activatedAt = activationCounter;
  } else if (engaged?.token === token) {
    // The right to write was just withdrawn (space paused, notepad turned
    // read-only). Discard rather than commit — landing ink at the exact moment
    // access ended is never what the deactivator intended.
    releaseDrawLock("surface-inactive", true);
  }
}

/** Commits the in-flight stroke so a submission exports/syncs it. */
export function flushDrawLockBeforeSubmit(): void {
  if (getDrawLockMode() !== "off" || engaged) releaseDrawLock("submit");
}

/**
 * Whether the lock is holding ink a submission would need.
 *
 * A pure query, separate from the flush, because committing the stroke is a
 * React state update several hops from the press handler: the press cannot
 * observe it in the same tick, so "is there work to submit?" has to be
 * answerable without committing anything first. Callers ask this before their
 * refusal guards and flush only once they have decided to go ahead.
 */
export function hasDrawLockInk(): boolean {
  return getDrawLockMode() !== "off" || engaged !== null;
}

/** Test seam. */
export function __resetDrawLockForTests(): void {
  engaged = null;
  registry.clear();
  spaceOwned = false;
  spacePressStartedAt = 0;
  activationCounter = 0;
  if (listenerRefCount > 0) removeGlobalListeners();
  listenerRefCount = 0;
  useDrawLockStore.setState({ mode: "off", engagedSurfaceId: null });
}
